'use client'

import Link from 'next/link'
import { useEffect, useRef, useState, type MouseEvent, type PointerEvent } from 'react'
import { Media } from '@/components/Media'
import { track } from '@/lib/analytics'
import type { Project } from '@/lib/portfolio'

/**
 * The home page's cases: three cards at a time on a desktop, sliding a whole
 * set at once.
 *
 * Every set is rendered in every slot and moved with a transform rather than
 * swapped, which is what makes the change a slide instead of a flicker: the
 * card leaving and the card arriving are both in the DOM, one going out to the
 * right while the other comes in from the left.
 *
 * The move takes two frames, and it has to. The arriving set is first *parked*
 * at the entry side with no transition at all; only on the next frame does it
 * get its duration and slide to zero. Skip the parking frame and the browser
 * interpolates from wherever the card last was — which reads as a jump, or as
 * a card sliding out the way it came in.
 *
 * The slots are staggered by 60ms, so the columns arrive as separate beats
 * rather than one block. The stagger is dropped on the parking frame: a delay
 * on a zero-duration move would hold the card visibly in the wrong place.
 */

/**
 * How many cards a set holds, which is a question about the screen.
 *
 * Three columns on a phone is three cards stacked in a column, and a slider
 * that moves three stacked cards at once shows one and hides two below the
 * fold. So the set shrinks with the viewport rather than the grid alone.
 *
 * It starts at three and is corrected after mount: the server has no viewport
 * to measure, and a first render that disagrees with the client is a hydration
 * error. Three is what a desktop gets, and a desktop is where the mismatch
 * would be visible.
 */
const BREAKPOINTS = [
    { query: '(min-width: 1024px)', perView: 3 },
    { query: '(min-width: 640px)', perView: 2 },
]

function usePerView(): number {
    const [perView, setPerView] = useState(3)

    useEffect(() => {
        const lists = BREAKPOINTS.map((breakpoint) => ({
            ...breakpoint,
            list: window.matchMedia(breakpoint.query),
        }))

        const measure = () => setPerView(lists.find(({ list }) => list.matches)?.perView ?? 1)

        measure()
        lists.forEach(({ list }) => list.addEventListener('change', measure))

        return () => lists.forEach(({ list }) => list.removeEventListener('change', measure))
    }, [])

    return perView
}

export function CaseSlider({ projects }: { projects: Project[] }) {
    const perView = usePerView()
    const sets = Math.max(1, Math.ceil(projects.length / perView))
    const [set, setSet] = useState(0)
    const [parked, setParked] = useState<number | null>(null)
    const [direction, setDirection] = useState(1)
    const moving = useRef(false)
    /** A move asked for while one was running, so no gesture is lost. */
    const pending = useRef(0)

    // Turning a phone sideways can leave the reader past the last set.
    const current = Math.min(set, sets - 1)
    /*
     * Where the track is heading, as opposed to where this render says it is.
     * A move queued behind another runs from the closure that started the
     * first one, and `current` there is the set *before* it — reading it would
     * send the second gesture to the place the first one already went.
     */
    const at = useRef(current)
    at.current = current

    if (projects.length === 0) {
        return null
    }

    /*
     * Dragging, by whatever is doing the dragging.
     *
     * Pointer events rather than touch events: the same handful of lines then
     * covers a thumb, a mouse and a pen. Before this the slider had touch
     * handlers only, so on a desktop the arrows were the whole interface —
     * a carousel that does not follow the cursor reads as broken.
     *
     * Nothing is prevented, so the page still scrolls vertically through the
     * grid; the gesture is only read once it is clearly sideways. A drag that
     * ends on a card would otherwise open it — the card is a link — so a
     * gesture that moved marks itself and the click that follows is swallowed.
     */
    const drag = useRef<{ x: number; y: number; id: number } | null>(null)
    const dragged = useRef(false)

    const DRAG = 48

    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        if (!event.isPrimary) {
            return
        }

        drag.current = { x: event.clientX, y: event.clientY, id: event.pointerId }
        dragged.current = false
    }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const start = drag.current

        if (start === null || event.pointerId !== start.id) {
            return
        }

        const dx = event.clientX - start.x

        if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(event.clientY - start.y)) {
            dragged.current = true
        }
    }

    const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
        const start = drag.current

        if (start === null || event.pointerId !== start.id) {
            return
        }

        drag.current = null

        if (!dragged.current) {
            return
        }

        const dx = event.clientX - start.x

        if (Math.abs(dx) >= DRAG) {
            // Dragging the cards to the left asks for what is to the right of
            // them, the way a page moves under a finger.
            move(dx < 0 ? 1 : -1)
        }
    }

    const onPointerCancel = () => {
        drag.current = null
    }

    const onClickCapture = (event: MouseEvent) => {
        if (!dragged.current) {
            return
        }

        event.preventDefault()
        event.stopPropagation()
        dragged.current = false
    }

    const move = (step: number) => {
        if (moving.current) {
            // Two swipes in a row used to produce one move: the second arrived
            // while the first was still running and was dropped on the floor,
            // which reads as a slider that skipped your gesture. The last one
            // asked for is remembered and runs when the track is free.
            pending.current = step

            return
        }

        track({ event: 'case_slider_move', slider_direction: step > 0 ? 'next' : 'previous' })

        const next = (at.current + step + sets) % sets
        at.current = next

        moving.current = true
        setDirection(step)
        setParked(next)

        // Two frames: one for the browser to accept the parked position, one
        // to start the transition from it.
        requestAnimationFrame(() =>
            requestAnimationFrame(() => {
                setSet(next)
                setParked(null)
                window.setTimeout(() => {
                    moving.current = false

                    const queued = pending.current
                    pending.current = 0

                    if (queued !== 0) {
                        move(queued)
                    }
                }, 950)
            }),
        )
    }

    // Forward moves the content to the left: the set that leaves goes out the
    // side the reader is heading towards, and the next one follows in behind
    // it. The other way round reads as going back, which is what it looked
    // like on a phone — one card, moving against the thumb that asked for it.
    const entering = direction > 0 ? 'translateX(100.5%)' : 'translateX(-100.5%)'
    const leaving = direction > 0 ? 'translateX(-100.5%)' : 'translateX(100.5%)'

    return (
        <>
            {/* Heading and arrows share a line, as the design has them. On a
             * phone there is no line to share: the heading takes the width it
             * needs and the arrows drop underneath rather than squeezing it
             * into three words a row. */}
            <div className="flex items-end justify-between gap-8 px-gutter pt-32 pb-10 max-sm:flex-col max-sm:items-start max-sm:gap-7">
                <h2
                    data-wave
                    className="m-0 max-w-[20ch] text-[clamp(26px,2.8vw,38px)] leading-[1.18] font-normal tracking-[-0.035em]"
                >
                    Projecten die live staan en dagelijks gebruikt worden.
                </h2>
                <div className="flex flex-none items-center gap-5">
                    <div className="flex flex-none gap-px border border-hair bg-hair">
                        <button
                            type="button"
                            onClick={() => move(-1)}
                            aria-label="Vorige projecten"
                            className="h-[46px] w-[46px] cursor-pointer border-0 bg-bg font-mono text-sm text-fg transition-colors duration-200 hover:bg-accent hover:text-on-accent"
                        >
                            ←
                        </button>
                        <button
                            type="button"
                            onClick={() => move(1)}
                            aria-label="Volgende projecten"
                            className="h-[46px] w-[46px] cursor-pointer border-0 bg-bg font-mono text-sm text-fg transition-colors duration-200 hover:bg-accent hover:text-on-accent"
                        >
                            →
                        </button>
                    </div>
                    <span className="font-mono text-[11px] text-faint">
                        {String(current + 1).padStart(2, '0')} / {String(sets).padStart(2, '0')}
                    </span>
                </div>
            </div>

            <div
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
                onClickCapture={onClickCapture}
                // A card is a link, and a link is draggable: without this a
                // mouse drag becomes the browser's own drag-and-drop and the
                // slider never sees the gesture.
                onDragStart={(event) => event.preventDefault()}
                className="grid cursor-grab touch-pan-y grid-cols-3 gap-px border-t border-hair bg-hair select-none active:cursor-grabbing max-lg:grid-cols-2 max-sm:grid-cols-1"
            >
                {Array.from({ length: perView }, (_, slot) => slot).map((slot) => (
                    <div key={slot} className="relative isolate min-h-[494px] overflow-hidden bg-bg">
                        {Array.from({ length: sets }, (_, index) => index).map((index) => {
                            const project = projects[index * perView + slot]

                            if (project === undefined) {
                                return null
                            }

                            const isParked = parked !== null && index === parked
                            const isShown = parked === null && index === current
                            // While a set is parked, the one it replaces stays
                            // where it is: it only leaves on the next frame.
                            const holds = parked !== null && index === current
                            const visible = isParked || isShown || holds

                            return (
                                <Link
                                    key={project.slug}
                                    href={project.url}
                                    // Out of the tab order while it is off-screen: a
                                    // slider that hands focus to a card nobody can see
                                    // scrolls the page sideways under the reader.
                                    tabIndex={isShown ? undefined : -1}
                                    aria-hidden={isShown ? undefined : true}
                                    className="absolute inset-0 flex flex-col bg-bg ease-[cubic-bezier(.76,0,.18,1)]"
                                    style={{
                                        zIndex: isParked || isShown ? 2 : 1,
                                        transform: isParked ? entering : visible ? 'translateX(0)' : leaving,
                                        transitionProperty: 'transform',
                                        transitionTimingFunction: 'cubic-bezier(.76,0,.18,1)',
                                        transitionDuration: isParked ? '0ms' : '900ms',
                                        transitionDelay: parked === null ? `${slot * 60}ms` : '0ms',
                                    }}
                                >
                                    <div className="relative aspect-4/3 flex-none overflow-hidden panel">
                                        <Media
                                            media={project.media}
                                            alt={project.title}
                                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                            className="h-full w-full object-cover"
                                        />
                                        {project.kind === '' ? null : (
                                            <span className="absolute top-3 right-3 bg-bg/60 px-[9px] py-[5px] font-mono text-[10px] tracking-[0.08em] backdrop-blur-[6px]">
                                                {project.kind}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-1 flex-col gap-3 px-gutter pt-6 pb-[30px]">
                                        <h3 className="m-0 text-[21px] font-normal tracking-[-0.03em]">
                                            {project.title}
                                        </h3>
                                        <p className="m-0 text-[14.5px] leading-relaxed text-pretty text-dim">
                                            {project.summary}
                                        </p>
                                        <span className="mt-1 font-mono text-[11px] text-accent">
                                            bekijk project →
                                        </span>
                                    </div>
                                </Link>
                            )
                        })}
                    </div>
                ))}
            </div>

            {/* Clear of the card above it: the last line a card carries is
             * "bekijk project", and the rule sat against it. */}
            <div aria-hidden="true" className="relative mt-5 h-px bg-hair">
                <span
                    className="absolute -top-px left-0 block h-[3px] bg-accent transition-[width] duration-600 ease-design"
                    style={{ width: `calc(${(100 / sets).toFixed(4)}% * ${current + 1})` }}
                />
            </div>
        </>
    )
}
